import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { COMMERCIAL_IMAGES } from "@/data/images";

const truckBullets = [
  "Freightliner & Isuzu commercial trucks",
  "Hydraulic liftgates — up to 3,300 lb capacity",
  "Dashcams + rearview cameras on every unit",
  "Load bars, e-track, and moving pads standard",
];

export function TrucksSection() {
  return (
    <>
      {/* TRUCKS INTRO */}
      <section className="py-20 lg:py-28 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">Our Trucks</div>
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-slate-900">
              Modern trucks. Purpose-built for commercial loads.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Our fleet is a mix of Freightliner and Isuzu commercial trucks — the sweet spot
              for commercial deliveries. Big enough for pallets and bulk retail freight, nimble
              enough for downtown docks, alleyways, and residential streets.
            </p>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Every truck is equipped with a hydraulic liftgate, load bars, e-track tie-downs,
              furniture pads, and a pallet jack. Cabs run high-visibility mirrors, dashcams,
              and telematics so both driver and dispatch see the same view of every trip.
            </p>
            <ul className="mt-6 space-y-3">
              {truckBullets.map((b) => (
                <li key={b} className="flex items-center gap-3 text-slate-800">
                  <CheckCircle2 className="h-4 w-4 text-primary-brand flex-none" />
                  <span className="text-sm">{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 gap-4"
          >
            <img src={COMMERCIAL_IMAGES.truck2} alt="Laronta Pro truck curbside" className="w-full h-64 object-cover border border-slate-200 col-span-2" data-testid="commercial-truck-2" />
            <img src={COMMERCIAL_IMAGES.autumn} alt="Truck on scenic highway" className="w-full h-52 object-cover border border-slate-200" data-testid="commercial-truck-autumn" />
            <img src={COMMERCIAL_IMAGES.fleetYard} alt="Fleet lineup" className="w-full h-52 object-cover border border-slate-200" data-testid="commercial-truck-fleet" />
          </motion.div>
        </div>
      </section>

      {/* FEATURED TRUCK BANNER */}
      <section className="relative border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 relative"
            >
              <img
                src={COMMERCIAL_IMAGES.truckFeature}
                alt="Laronta Pro truck ready for commercial dispatch"
                data-testid="commercial-feature-image"
                className="w-full h-auto object-cover border border-slate-200"
                loading="lazy"
              />
              <div className="absolute top-5 left-5 bg-primary-brand text-white px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold">
                Ready to Roll
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">
                Purpose-Built Commercial Trucks
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-slate-900">
                Clean. Maintained. On the road.
              </h2>
              <p className="mt-5 text-slate-700 leading-relaxed">
                Every unit in our commercial fleet is serviced on a strict maintenance schedule,
                inspected pre-trip, and driven by a CDL-certified pro. When your freight rolls
                out with Laronta Pro, it rolls out on equipment we'd trust with our own name —
                because it literally has our name on it.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-slate-800">
                <li className="flex items-center gap-3"><span className="h-1.5 w-6 bg-primary-brand" /> Freightliner M2 commercial trucks</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-6 bg-primary-brand" /> Weekly wash + monthly deep detail</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-6 bg-primary-brand" /> Full liftgate + cargo securement kit on every unit</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-6 bg-primary-brand" /> GPS tracked from dispatch to delivery</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
